import { useState } from 'react';
import { BookOpen, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { GUIDES, type Guide } from './guides';

/** The book icon: opens the page's guide. */
export function LearnButton({ guide }: { guide: keyof typeof GUIDES }) {
  const [open, setOpen] = useState(false);
  const g = GUIDES[guide];
  if (!g) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`How this page works: ${g.title}`}
        title="How this page works"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30"
      >
        <BookOpen className="w-4 h-4" />
        Learn
      </button>
      {open && <LearnGuide guide={g} onClose={() => setOpen(false)} />}
    </>
  );
}

/** One picture and one line per step; Back / Next / Done. */
export function LearnGuide({
  guide,
  onClose,
}: {
  guide: Guide;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const step = guide.steps[index];
  const last = index === guide.steps.length - 1;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={guide.title}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3"
    >
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <BookOpen className="w-4 h-4 text-primary-600 dark:text-primary-400" />
          <h2 className="flex-1 text-sm font-semibold text-slate-900 dark:text-white">
            {guide.title}
          </h2>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {index + 1} of {guide.steps.length}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-3 space-y-3">
          <img
            src={step.image}
            alt={step.title}
            className="w-full rounded-lg border border-slate-200 dark:border-slate-700"
          />
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              {step.title}
            </h3>
            <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
              {step.text}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <div className="flex-1 flex items-center gap-1" aria-hidden="true">
            {guide.steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full ${
                  i === index
                    ? 'w-4 bg-primary-600'
                    : 'w-1.5 bg-slate-300 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <button
            type="button"
            onClick={() => (last ? onClose() : setIndex((i) => i + 1))}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
          >
            {last ? 'Done' : 'Next'}
            {!last && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
